'use client'

import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react'
import { generateRandomKey } from '../../../../MyStichtingOpenHelpers/src/static'
import { NotificationModel, NotificationsNotifyOptions, NotificationVariant } from '../../types'

export type NotificationProviderValue = {
    remove: (id: string) => void
    success: (options: NotificationsNotifyOptions) => void
    warning: (options: NotificationsNotifyOptions) => void
    error: (options: NotificationsNotifyOptions) => void
    info: (options: NotificationsNotifyOptions) => void
    notifications: NotificationModel[]
}

export const NotificationContext = createContext<NotificationProviderValue>({
    remove: () => null,
    success: () => null,
    warning: () => null,
    error: () => null,
    info: () => null,
    notifications: [],
})

/** @deprecated use useContext(NotificationContext) */
export const useNotification = (): NotificationProviderValue => {
    return useContext(NotificationContext)
}

interface NotificationProviderProps {
    children: ReactNode
}

/**
 * This provider is responsible for receiving and showing fixed notifications.
 */
export const NotificationProvider = ({ children }: NotificationProviderProps) => {
    const [notifications, setNotifications] = useState<NotificationModel[]>([])

    const notify = (options: NotificationsNotifyOptions) => {
        const id = generateRandomKey()
        setNotifications((previousNotifications) => [
            ...previousNotifications,
            {
                id,
                ...options,
                variant: options.variant ?? NotificationVariant.INFO,
            },
        ])
    }

    const success = (options: NotificationsNotifyOptions) => {
        notify({ ...options, variant: NotificationVariant.SUCCESS })
    }

    const warning = (options: NotificationsNotifyOptions) => {
        notify({ ...options, variant: NotificationVariant.WARNING })
    }

    const error = (options: NotificationsNotifyOptions) => {
        notify({ ...options, variant: NotificationVariant.ERROR })
    }

    const info = (options: NotificationsNotifyOptions) => {
        notify({ ...options, variant: NotificationVariant.INFO })
    }

    const remove = (id: string) => {
        setNotifications((previousNotifications) =>
            previousNotifications.filter((notification) => notification.id !== id)
        )
    }

    const providerValue = useMemo(
        () => ({
            remove,
            success,
            warning,
            error,
            info,
            notifications,
        }),
        [notifications]
    )

    return <NotificationContext.Provider value={providerValue}>{children}</NotificationContext.Provider>
}
